/**
 * L'email con cui si chiede di attivare l'abbonamento.
 *
 * [sc-48583 / task 48586] Stava dentro `TrialBanner.vue`, e con la modale di
 * fine prova sarebbe diventata la stessa cosa scritta in due punti: due
 * oggetti, due corpi, e prima o poi due testi diversi per la stessa richiesta.
 */
import { PLAN_START } from "@/config/plans";

/**
 * @param {string} companyName nome dell'azienda, se lo store ce l'ha
 * @returns {string} un mailto già scritto, o "" se non c'è a chi scrivere
 */
export function mailtoAttivazione(companyName) {
  const email = process.env.VUE_APP_SALES_EMAIL;
  // Senza un indirizzo configurato non si mostra nessun pulsante: meglio
  // niente che un bottone che scrive a nessuno.
  if (!email) return "";

  // [sc-48583 / task 48587] Oggetto e corpo già scritti: chi riceve la
  // richiesta deve capire da chi arriva e cosa serve senza rispondere per
  // chiederlo. Il nome viene dal marchio in store; se non c'è ancora, la
  // frase resta buona lo stesso.
  const nome = typeof companyName === "string" ? companyName.trim() : "";
  // [revisione 48654] L'oggetto resta «Attivazione abbonamento»: e' quello che
  // la casella commerciale legge per smistare, e il gesto e' quello. Il corpo
  // invece chiede i dettagli, perche' e' la prima cosa che il cliente vuole
  // sapere - la finestra di fine prova dice che il costo sta nel contratto
  // «che ti manderemo via mail sotto tua richiesta», e questa e' la richiesta.
  const oggetto = `Attivazione abbonamento CRM ${PLAN_START.name}`;
  const corpo = nome
    ? `Vorrei conoscere i dettagli del piano CRM ${PLAN_START.name} per ${nome}, grazie.`
    : `Vorrei conoscere i dettagli del piano CRM ${PLAN_START.name}, grazie.`;

  return `mailto:${email}?subject=${encodeURIComponent(
    oggetto
  )}&body=${encodeURIComponent(corpo)}`;
}

/**
 * L'indirizzo dove si prenota una call.
 *
 * [sc-46314] Una variabile e non un indirizzo cablato: l'agenda commerciale
 * può cambiare — oggi è la pagina appuntamenti di Google Calendar — e
 * spostarla non deve voler dire rifare un deploy di codice. Vuota: nessun
 * pulsante, come per `mailtoAttivazione`.
 *
 * @returns {string} l'URL, oppure "" se non c'è o non è sicuro
 */
export function linkPrenotaCall() {
  const url = (process.env.VUE_APP_BOOK_CALL_URL || "").trim();
  if (!url) return "";
  // Solo https: la CTA porta fuori dal gestionale, e un `javascript:` in una
  // variabile d'ambiente sarebbe un buco aperto con le nostre mani.
  return url.startsWith("https://") ? url : "";
}

/**
 * L'email per una domanda, che non è una richiesta di attivazione.
 *
 * [sc-46314] Sono due intenzioni diverse e meritano due oggetti diversi: chi
 * sta imparando il gestionale al terzo giorno di prova non sta chiedendo di
 * abbonarsi, e ricevere «Attivazione abbonamento» da chi voleva solo capire
 * come si carica il magazzino manda fuori strada tutti e due.
 *
 * @param {string} companyName nome dell'azienda, se lo store ce l'ha
 * @returns {string} un mailto già scritto, o "" se non c'è a chi scrivere
 */
export function mailtoAssistenza(companyName) {
  const email = process.env.VUE_APP_SALES_EMAIL;
  if (!email) return "";

  const nome = typeof companyName === "string" ? companyName.trim() : "";
  const oggetto = `Una domanda su CRM ${PLAN_START.name}`;
  const corpo = nome
    ? `Sto provando CRM ${PLAN_START.name} per ${nome} e avrei una domanda:`
    : `Sto provando CRM ${PLAN_START.name} e avrei una domanda:`;

  return `mailto:${email}?subject=${encodeURIComponent(
    oggetto
  )}&body=${encodeURIComponent(corpo)}`;
}

/**
 * L'email per chiedere un operatore in più.
 *
 * [sc-48692] Gli operatori li crea liberotratto dalla console: il cliente
 * chiede, e l'oggetto dice subito cosa serve senza bisogno di rispondere.
 *
 * @param {string} companyName nome dell'azienda, se lo store ce l'ha
 * @returns {string} un mailto già scritto, o "" se non c'è a chi scrivere
 */
export function mailtoNuovoOperatore(companyName) {
  const email = process.env.VUE_APP_SALES_EMAIL;
  if (!email) return "";

  const nome = typeof companyName === "string" ? companyName.trim() : "";
  const oggetto = `Nuovo operatore per CRM ${PLAN_START.name}`;
  const corpo = nome
    ? `Vorrei aggiungere un operatore per ${nome}. Indirizzo email del nuovo operatore: `
    : `Vorrei aggiungere un operatore. Indirizzo email del nuovo operatore: `;

  return `mailto:${email}?subject=${encodeURIComponent(
    oggetto
  )}&body=${encodeURIComponent(corpo)}`;
}
